import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Box, IconButton, Typography, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';

// Generation colors - LIGHT pastel colors for descendants
const GENERATION_COLORS = {
  root: '#00FFFF',      // Cyan - Founder/Root (Mwendwa Kitavi)
  spouse: '#FFB6C1',    // Light Pink - Spouses
  kaweto: '#DDA0DD',    // Plum/Light Purple - Kaweto
  child: '#98FB98',     // Pale Green - Children
  grandchild: '#FFFACD', // Lemon Chiffon - Grandchildren
};

// Depth-based colors for deeper generations - EXTENDED to 20+ with distinct colors
const DEPTH_COLORS = [
  '#00FFFF',  // 0 - Cyan (Mwendwa Kitavi)
  '#FFB6C1',  // 1 - Light Pink (Spouses/Wives)
  '#98FB98',  // 2 - Pale Green (Children)
  '#FFFACD',  // 3 - Lemon Chiffon (Grandchildren)
  '#FFE4B5',  // 4 - Moccasin (Great-grandchildren)
  '#E6E6FA',  // 5 - Lavender
  '#B0E0E6',  // 6 - Powder Blue
  '#FFDAB9',  // 7 - Peach Puff
  '#D8BFD8',  // 8 - Thistle
  '#F0FFF0',  // 9 - Honeydew
  '#FFF0F5',  // 10 - Lavender Blush
  '#F5FFFA',  // 11 - Mint Cream
  // Extended colors for Gen 12+
  '#87CEEB',  // 12 - Sky Blue
  '#F0E68C',  // 13 - Khaki
  '#DDA0DD',  // 14 - Plum
  '#90EE90',  // 15 - Light Green
  '#FFD700',  // 16 - Gold
  '#FF7F50',  // 17 - Coral
  '#00CED1',  // 18 - Dark Turquoise
  '#FF69B4',  // 19 - Hot Pink
  '#32CD32',  // 20 - Lime Green
  '#BA55D3',  // 21 - Medium Orchid
  '#FF6347',  // 22 - Tomato
  '#4682B4',  // 23 - Steel Blue
  '#9ACD32',  // 24 - Yellow Green
  '#EE82EE',  // 25 - Violet
];

// Function to get color - cycles if beyond array length
const getDepthColor = (depth) => {
  if (depth < DEPTH_COLORS.length) {
    return DEPTH_COLORS[depth];
  }
  // Cycle through colors 12-25 for very deep generations
  const cycleIndex = 12 + ((depth - 12) % 14);
  return DEPTH_COLORS[cycleIndex];
};

// Ancestor color (above Mwendwa Kitavi)
const ANCESTOR_COLOR = '#A0A0A0';  // Gray for ancestors

// Mwendwa Kitavi founder color
const FOUNDER_COLOR = '#00FFFF';  // Cyan for Mwendwa Kitavi

// List of known ancestors (above Mwendwa Kitavi)
const ANCESTORS = [
  'Bantu People',
  'Kamba Tribe', 
  'Ithanga Clan',
  'Aumon Clan',
  'Kitavi Seleitan',
  'Munyanyaki',
  'Kakaa Munyanyeki',
  'Muny\'athika Kakii',
  'Ndingi Muny\'athika',
  'Kithue Ndingi',
  'Mwanango Kithue',
  'Kitavi Mwanango'
];

// Sort children by birthOrder (lower numbers first, undefined last)
const sortByBirthOrder = (children) => {
  if (!children || !Array.isArray(children)) return [];
  return [...children].sort((a, b) => {
    const orderA = a.birthOrder ?? 9999;
    const orderB = b.birthOrder ?? 9999;
    if (orderA !== orderB) return orderA - orderB;
    // If same birthOrder, sort by name as fallback
    return (a.name || '').localeCompare(b.name || '');
  });
};

// Cloudinary face-centered thumbnail: small, fast, cropped to the face
const photoThumb = (url, size = 80) =>
  url && typeof url === 'string' && url.includes('/upload/')
    ? url.replace('/upload/', `/upload/w_${size},h_${size},c_fill,g_face/`)
    : url;


const getNodeColor = (type, depth, name) => {
  // Check if this is Mwendwa Kitavi (founder) - check first
  if (name && (name === 'Mwendwa Kitavi' || name === 'Chief Mwendwa Kitavi' || name.includes('Mwendwa Kitavi'))) {
    return FOUNDER_COLOR;
  }
  
  // Check if this is an ancestor (above Mwendwa Kitavi)
  if (name && ANCESTORS.some(ancestor => name.includes(ancestor))) {
    return ANCESTOR_COLOR;
  }
  
  // For spouses and kawetos, use their specific colors
  if (type === 'spouse') return GENERATION_COLORS.spouse;
  if (type === 'kaweto') return GENERATION_COLORS.kaweto;
  
  // For descendants, use depth-based generation colors
  // depth 0 = root level in current view
  // We want generation colors based on how deep below the current root
  return getDepthColor(Math.max(depth, 0));
};

const transformData = (data, selectedPerson) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.log('No data provided to transformData in FlowChartView2');
    return null;
  }

  // DEBUG: Check if birthOrder exists in raw data
  const peopleWithBirthOrder = data.filter(p => p.birthOrder);
  console.log('=== FLOWCHARTVIEW2 BIRTH ORDER DEBUG ===');
  console.log('People with birthOrder:', peopleWithBirthOrder.map(p => ({
    name: p.name,
    birthOrder: p.birthOrder
  })));

  console.log('Raw data in FlowChartView2:', data);
  console.log('Selected person in FlowChartView2:', selectedPerson);

  // Create a map of people for quick lookup
  const personMap = {};
  data.forEach((person) => {
    if (person && person._id) {
      personMap[person._id] = { ...person, children: [], spouses: [], kawetos: [] };
    } else {
      console.log('Invalid person skipped in personMap:', person);
    }
  });

  // Populate relationships in the personMap
  data.forEach((person) => {
    if (!person || !person._id) return;

    // Populate spouses
    if (person.spouses && Array.isArray(person.spouses)) {
      person.spouses.forEach((spouse) => {
        const spouseId = spouse?._id || spouse;
        const spouseRecord = personMap[spouseId];
        if (spouseRecord) {
          personMap[person._id].spouses.push(spouseRecord);
        } else {
          console.log(`Spouse with ID ${spouseId} not found for person ${person.name || 'unknown'}`);
        }
      });
    }

    // Populate kawetos
    if (person.kawetos && Array.isArray(person.kawetos)) {
      person.kawetos.forEach((kaweto) => {
        const kawetoId = kaweto?._id || kaweto;
        const kawetoRecord = personMap[kawetoId];
        if (kawetoRecord) {
          personMap[person._id].kawetos.push(kawetoRecord);
        } else {
          console.log(`Kaweto with ID ${kawetoId} not found for person ${person.name || 'unknown'}`);
        }
      });
    }

    // Populate children
    if (person.children && Array.isArray(person.children)) {
      person.children.forEach((child) => {
        const childId = child?._id || child;
        const childRecord = personMap[childId];
        if (childRecord) {
          personMap[person._id].children.push(childRecord);
        } else {
          console.log(`Child with ID ${childId} not found for person ${person.name || 'unknown'}`);
        }
      });
    }
  });

  // Sort all children arrays by birthOrder for consistent display
  Object.values(personMap).forEach(person => {
    if (person.children && person.children.length > 1) {
      const beforeSort = person.children.map(c => ({ name: c.name, birthOrder: c.birthOrder }));
      person.children = sortByBirthOrder(person.children);
      const afterSort = person.children.map(c => ({ name: c.name, birthOrder: c.birthOrder }));
      console.log(`Sorted children of ${person.name}:`, { before: beforeSort, after: afterSort });
    }
  });

  // Select the root person - IMPROVED: Try multiple methods to find the person
  let rootPerson = null;
  
  if (selectedPerson) {
    // Method 1: Try by _id directly
    if (selectedPerson._id && personMap[selectedPerson._id]) {
      rootPerson = personMap[selectedPerson._id];
      console.log('Found root person by _id:', rootPerson.name);
    }
    
    // Method 2: Try by name if _id lookup failed
    if (!rootPerson && selectedPerson.name) {
      const foundByName = data.find(p => p.name === selectedPerson.name);
      if (foundByName && personMap[foundByName._id]) {
        rootPerson = personMap[foundByName._id];
        console.log('Found root person by name:', rootPerson.name);
      }
    }
    
    // Method 3: Try partial name match (case-insensitive)
    if (!rootPerson && selectedPerson.name) {
      const searchName = selectedPerson.name.toLowerCase();
      const foundByPartialName = data.find(p => 
        p.name && p.name.toLowerCase().includes(searchName)
      );
      if (foundByPartialName && personMap[foundByPartialName._id]) {
        rootPerson = personMap[foundByPartialName._id];
        console.log('Found root person by partial name:', rootPerson.name);
      }
    }
  }
  
  // Fallback to Mwendwa Kitavi or first person
  if (!rootPerson) {
    rootPerson = data.find((p) => p.name === 'Mwendwa Kitavi' && !p.mother && !p.father && !p.socialParent);
    if (rootPerson && personMap[rootPerson._id]) {
      rootPerson = personMap[rootPerson._id];
      console.log('Using Mwendwa Kitavi as root');
    } else if (data[0] && personMap[data[0]._id]) {
      rootPerson = personMap[data[0]._id];
      console.log('Using first person as root:', rootPerson.name);
    }
  }
  
  if (!rootPerson) {
    console.log('No root person found in FlowChartView2');
    return null;
  }

  console.log('Selected root person:', rootPerson.name, 'with', rootPerson.children?.length || 0, 'children');

  // Recursive function to build the tree
  const buildTreeNode = (person, level = 0, isSpouseView = false) => {
    if (!person || !person._id || !person.name) {
      console.log('Invalid person in buildTreeNode:', person);
      return null;
    }

    const node = {
      name: person.name,
      _id: person._id,
      role: person.role || 'unknown',
      type: isSpouseView ? 'spouse' : (level === 0 ? 'root' : (level === 1 ? 'child' : 'grandchild')),
      children: [],
      spouses: [],
      kawetos: [],
    };

    console.log(`Building tree node for ${person.name} (Level: ${level}, isSpouseView: ${isSpouseView}, children: ${person.children?.length || 0})`);

    // For the root (Mwendwa Kitavi) when no person is selected
    if (!selectedPerson && person.name === 'Mwendwa Kitavi') {
      if (person.spouses && Array.isArray(person.spouses)) {
        const spouseNodes = person.spouses
          .map((spouse) => {
            if (!spouse || !spouse._id || !spouse.name) {
              console.log('Invalid spouse skipped:', spouse);
              return null;
            }
            const spouseNode = {
              name: spouse.name,
              _id: spouse._id,
              role: spouse.role || 'unknown',
              type: 'spouse',
              children: [],
              spouses: [],
              kawetos: [],
            };
            // Add children under each spouse - SORTED BY BIRTH ORDER
            if (spouse.children && Array.isArray(spouse.children)) {
              const childNodes = sortByBirthOrder(spouse.children)
                .map((child) => buildTreeNode(child, level + 1))
                .filter((node) => node !== null);
              spouseNode.children.push(...childNodes);
            }
            // Add kawetos for the spouse
            if (spouse.kawetos && Array.isArray(spouse.kawetos)) {
              const kawetoNodes = spouse.kawetos
                .map((kaweto) => {
                  if (!kaweto || !kaweto._id || !kaweto.name) {
                    console.log('Invalid kaweto skipped:', kaweto);
                    return null;
                  }
                  return {
                    name: kaweto.name,
                    _id: kaweto._id,
                    role: kaweto.role || 'KAWETO',
                    type: 'kaweto',
                    children: [],
                    spouses: [],
                    kawetos: [],
                  };
                })
                .filter((node) => node !== null);
              spouseNode.kawetos.push(...kawetoNodes);
            }
            return spouseNode;
          })
          .filter((node) => node !== null);
        node.children.push(...spouseNodes);
      }
      // Add kawetos directly under Mwendwa Kitavi
      if (person.kawetos && Array.isArray(person.kawetos)) {
        const kawetoNodes = person.kawetos
          .map((kaweto) => {
            if (!kaweto || !kaweto._id || !kaweto.name) {
              console.log('Invalid kaweto skipped:', kaweto);
              return null;
            }
            const kawetoNode = {
              name: kaweto.name,
              _id: kaweto._id,
              role: kaweto.role || 'KAWETO',
              type: 'kaweto',
              children: [],
              spouses: [],
              kawetos: [],
            };
            // Add children under each kaweto - SORTED BY BIRTH ORDER
            if (kaweto.children && Array.isArray(kaweto.children)) {
              const childNodes = sortByBirthOrder(kaweto.children)
                .map((child) => buildTreeNode(child, level + 1))
                .filter((node) => node !== null);
              kawetoNode.children.push(...childNodes);
            }
            return kawetoNode;
          })
          .filter((node) => node !== null);
        node.children.push(...kawetoNodes);
      }
      return node;
    }

    // If this is a spouse view, show children and kawetos
    if (isSpouseView) {
      if (person.children && Array.isArray(person.children)) {
        const childNodes = sortByBirthOrder(person.children)
          .map((child) => buildTreeNode(child, level + 1))
          .filter((node) => node !== null);
        node.children.push(...childNodes);
      }
      if (person.kawetos && Array.isArray(person.kawetos)) {
        const kawetoNodes = person.kawetos
          .map((kaweto) => {
            if (!kaweto || !kaweto._id || !kaweto.name) {
              console.log('Invalid kaweto skipped:', kaweto);
              return null;
            }
            return {
              name: kaweto.name,
              _id: kaweto._id,
              role: kaweto.role || 'KAWETO',
              type: 'kaweto',
              children: [],
              spouses: [],
              kawetos: [],
            };
          })
          .filter((node) => node !== null);
        node.kawetos.push(...kawetoNodes);
      }
      return node;
    }

    // Handle all spouses and kawetos for non-Mwendwa Kitavi nodes
    if (person.spouses && Array.isArray(person.spouses) && person.name !== 'Mwendwa Kitavi') {
      node.spouses = person.spouses
        .map((spouse) => {
          if (!spouse || !spouse._id || !spouse.name) {
            console.log('Invalid spouse skipped:', spouse);
            return null;
          }
          return {
            name: spouse.name,
            _id: spouse._id,
            role: spouse.role || 'unknown',
            type: 'spouse',
            children: [],
            spouses: [],
            kawetos: [],
          };
        })
        .filter((node) => node !== null);
    }

    if (person.kawetos && Array.isArray(person.kawetos) && person.name !== 'Mwendwa Kitavi') {
      node.kawetos = person.kawetos
        .map((kaweto) => {
          if (!kaweto || !kaweto._id || !kaweto.name) {
            console.log('Invalid kaweto skipped:', kaweto);
            return null;
          }
          return {
            name: kaweto.name,
            _id: kaweto._id,
            role: kaweto.role || 'KAWETO',
            type: 'kaweto',
            children: [],
            spouses: [],
            kawetos: [],
          };
        })
        .filter((node) => node !== null);
    }

    // Add children - SORTED BY BIRTH ORDER
    if (person.children && Array.isArray(person.children)) {
      const childNodes = sortByBirthOrder(person.children)
        .map((child) => buildTreeNode(child, level + 1))
        .filter((node) => node !== null);
      node.children.push(...childNodes);
    }

    return node;
  };

  const transformed = buildTreeNode(rootPerson, 0, selectedPerson && selectedPerson.type === 'spouse');
  if (!transformed) {
    console.log('Failed to transform data: root person is invalid');
    return null;
  }

  console.log('Transformed tree data in FlowChartView2:', transformed);
  return transformed;
};

// Function to wrap text within a specified width
const wrapText = (text, width) => {
  if (!text || typeof text !== 'string') {
    return { lines: ['Unknown'], lineHeight: 14 };
  }

  const words = text.split(/\s+/).reverse();
  let word;
  let line = [];
  const lines = [];
  const lineHeight = 14;

  while ((word = words.pop())) {
    line.push(word);
    const testLine = line.join(' ');
    const testWidth = getTextWidth(testLine, '12px sans-serif');
    if (testWidth > width && line.length > 1) {
      line.pop();
      lines.push(line.join(' '));
      line = [word];
    }
  }
  if (line.length > 0) {
    lines.push(line.join(' '));
  }
  return { lines, lineHeight };
};

// Function to calculate text width
const getTextWidth = (text, font) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = font;
  return context.measureText(text).width;
};

const FlowChartView2 = ({ data, onPersonClick, onBack, selectedPerson }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const currentContainer = containerRef.current;
    
    const updateDimensions = () => {
      if (currentContainer) {
        const { width, height } = currentContainer.getBoundingClientRect();
        if (width > 0 && height > 0) {
          setDimensions({ width, height });
          setIsReady(true);
        }
      }
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    if (currentContainer) {
      resizeObserver.observe(currentContainer);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isReady || dimensions.width <= 0 || dimensions.height <= 0) {
      return;
    }

    const svgElement = d3.select(svgRef.current);
    svgElement.selectAll('*').remove();

    if (!data || data.length === 0) {
      svgElement
        .append('text')
        .attr('x', dimensions.width / 2)
        .attr('y', dimensions.height / 2)
        .attr('text-anchor', 'middle')
        .text('No data to display in FlowChartView2')
        .style('font-size', '16px')
        .style('fill', '#333');
      return;
    }

    const treeData = transformData(data, selectedPerson);
    if (!treeData) {
      svgElement
        .append('text')
        .attr('x', dimensions.width / 2)
        .attr('y', dimensions.height / 2)
        .attr('text-anchor', 'middle')
        .text('No tree data to display in FlowChartView2')
        .style('font-size', '16px')
        .style('fill', '#333');
      return;
    }

    const margin = { top: 30, right: 30, bottom: 30, left: 30 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    if (width <= 0 || height <= 0) {
      return;
    }

    svgElement
      .attr('width', dimensions.width)
      .attr('height', dimensions.height);

    const g = svgElement.append('g');

    // Define the tree layout with adjusted node separation for larger nodes
    const treeLayout = d3.tree()
      .nodeSize([288, 96]); // Adjusted for larger nodes and kaweto

    // Convert treeData to a hierarchy
    const root = d3.hierarchy(treeData, (d) => d.children);
    treeLayout(root);

    // Adjust positions for all spouses and kawetos
    const nodes = root.descendants();
    const spouseNodes = [];
    const kawetoNodes = [];
    const nodeWidth = 120; // Node width
    const kawetoOffset = nodeWidth + 10; // Offset for kaweto nodes

    nodes.forEach((d) => {
      // Handle spouses
      if (d.data.spouses && d.data.spouses.length > 0 && d.data.name !== 'Mwendwa Kitavi') {
        d.spouseNodes = d.data.spouses.map((spouse, index) => {
          let xPosition;
          if (d.data.spouses.length === 2) {
            xPosition = index === 0 ? d.x - 144 : d.x + 144;
          } else {
            xPosition = d.x + 144 * (index + 1);
          }
          const spouseNode = {
            data: spouse,
            x: xPosition,
            y: d.y,
            depth: d.depth,
            parent: d.parent,
            isSpouse: true,
          };
          return spouseNode;
        });
        spouseNodes.push(...d.spouseNodes);
      }
      // Handle kawetos
      if (d.data.kawetos && d.data.kawetos.length > 0) {
        d.kawetoNodes = d.data.kawetos.map((kaweto, index) => {
          const kawetoNode = {
            data: kaweto,
            x: d.x + kawetoOffset * (index + 1), // Position kaweto to the right
            y: d.y,
            depth: d.depth,
            parent: d.parent,
            isKaweto: true,
          };
          return kawetoNode;
        });
        kawetoNodes.push(...d.kawetoNodes);
      }
    });

    // Combine all nodes for rendering
    const allNodes = [...nodes, ...spouseNodes, ...kawetoNodes];

    // Calculate the bounding box of the tree to center it
    const xValues = allNodes.map((d) => d.x);
    const yValues = allNodes.map((d) => d.y);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);
    const treeWidth = maxX - minX || 120;
    const treeHeight = maxY - minY || 80;

    // Calculate initial scale to fit the tree within the viewport
    // But ensure it's not too small to see
    const scaleX = width / (treeWidth + 60);
    const scaleY = height / (treeHeight + 60);
    let initialScale = Math.min(scaleX, scaleY, 1);
    
    // Ensure minimum scale of 0.4 so tree is always visible
    // and boost small scales for better initial view
    initialScale = Math.max(initialScale * 2.0, 0.4);
    initialScale = Math.min(initialScale, 2.5);

    const centerX = (width - treeWidth * initialScale) / 2 - minX * initialScale + margin.left;
    const centerY = (height - treeHeight * initialScale) / 2 - minY * initialScale + margin.top;

    g.attr('transform', `translate(${centerX},${centerY}) scale(${initialScale})`);

    const zoom = d3.zoom()
      .scaleExtent([0.3, 3])
      .filter((event) => !event.target.closest('.MuiIconButton-root'))
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svgElement
      .call(zoom)
      .call(zoom.transform, d3.zoomIdentity.translate(centerX, centerY).scale(initialScale));

    // Draw links between nodes (parent to child) with sharp corners
    g.selectAll('.link')
      .data(root.links())
      .enter()
      .append('path')
      .attr('d', (d) => {
        const sourceX = d.source.x;
        const sourceY = d.source.y + 30; // Bottom of source node
        const targetX = d.target.x;
        const targetY = d.target.y - 30; // Top of target node
        const midY = sourceY + (targetY - sourceY) / 2;
        return `M${sourceX},${sourceY} V${midY} H${targetX} V${targetY}`;
      })
      .style('fill', 'none')
      .style('stroke', '#66CDAA') // Medium Aquamarine for parent-child
      .style('stroke-width', 3)
      .style('stroke-dasharray', 'none');

    // Draw links between persons and their spouses with sharp corners
    g.selectAll('.spouse-link')
      .data(nodes.filter((d) => d.data.spouses && d.data.spouses.length > 0 && d.data.name !== 'Mwendwa Kitavi'))
      .enter()
      .selectAll('path')
      .data((d) => d.spouseNodes.map((spouseNode) => ({ source: d, target: spouseNode })))
      .enter()
      .append('path')
      .attr('d', (d) => {
        const sourceX = d.source.x + nodeWidth / 2;
        const sourceY = d.source.y;
        const targetX = d.target.x - nodeWidth / 2;
        return `M${sourceX},${sourceY} H${targetX}`;
      })
      .style('fill', 'none')
      .style('stroke', '#FFB6C1') // Light Pink for spouse
      .style('stroke-width', 3)
      .style('stroke-dasharray', 'none');

    // Draw links between persons and their kawetos with sharp corners
    g.selectAll('.kaweto-link')
      .data(nodes.filter((d) => d.kawetoNodes && d.kawetoNodes.length > 0))
      .enter()
      .selectAll('path')
      .data((d) => d.kawetoNodes.map((kawetoNode) => ({ source: d, target: kawetoNode })))
      .enter()
      .append('path')
      .attr('d', (d) => {
        const sourceX = d.source.x + nodeWidth / 2;
        const sourceY = d.source.y;
        const targetX = d.target.x - nodeWidth / 2;
        return `M${sourceX},${sourceY} H${targetX}`;
      })
      .style('fill', 'none')
      .style('stroke', '#DDA0DD') // Plum for kaweto
      .style('stroke-width', 3)
      .style('stroke-dasharray', '8,4'); // Dashed for kaweto

    // Draw nodes
    const node = g
      .selectAll('.node')
      .data(allNodes)
      .enter()
      .append('g')
      .attr('transform', (d) => `translate(${d.x},${d.y})`)
      .on('click', (event, d) => {
        onPersonClick({ _id: d.data._id, type: d.data.type, role: d.data.role });
      });

    // Photo lookup from the raw people data
    const photoById = {};
    (data || []).forEach((p) => {
      if (p && p._id && p.photoPath) photoById[p._id] = p.photoPath;
    });
    const photoR = 13;

    // Draw rectangles (boxes) with rounded edges and generation-based colors
    node.append('rect')
      .attr('width', nodeWidth)
      .attr('height', 60)
      .attr('x', -nodeWidth / 2)
      .attr('y', -30)
      .attr('rx', 15) // Rounded corners
      .attr('ry', 15) // Rounded corners
      .style('fill', (d) => getNodeColor(d.data.type, d.depth, d.data.name))
      .style('stroke', '#333') // Dark border for contrast
      .style('stroke-width', 2)
      .style('cursor', 'pointer')
      .style('filter', 'drop-shadow(0 3px 6px rgba(0,0,0,0.3))');

    // Face medallion on nodes that have a photo
    const photoNodes2 = node.filter(d => Boolean(photoById[d.data._id]));
    photoNodes2.append('clipPath')
      .attr('id', d => `nodeclip2-${d.data._id}`)
      .append('circle')
      .attr('cx', -nodeWidth / 2 + photoR + 5)
      .attr('cy', 0)
      .attr('r', photoR);
    photoNodes2.append('image')
      .attr('href', d => photoThumb(photoById[d.data._id]))
      .attr('x', -nodeWidth / 2 + 5)
      .attr('y', -photoR)
      .attr('width', photoR * 2)
      .attr('height', photoR * 2)
      .attr('preserveAspectRatio', 'xMidYMid slice')
      .attr('clip-path', d => `url(#nodeclip2-${d.data._id})`);
    photoNodes2.append('circle')
      .attr('cx', -nodeWidth / 2 + photoR + 5)
      .attr('cy', 0)
      .attr('r', photoR)
      .attr('fill', 'none')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5);

    // Draw text with wrapping
    node.each(function (d) {
      const nodeGroup = d3.select(this);
      const hasPhoto = Boolean(photoById[d.data._id]);
      const textWidth = hasPhoto ? nodeWidth - photoR * 2 - 16 : nodeWidth - 10;
      const textX = hasPhoto ? photoR + 1 : 0;
      const { lines, lineHeight } = wrapText(d.data.name, textWidth);
      const totalHeight = lines.length * lineHeight;

      lines.forEach((line, i) => {
        nodeGroup
          .append('text')
          .attr('x', textX)
          .attr('dy', `${i * lineHeight - totalHeight / 2 + lineHeight / 2}px`)
          .attr('text-anchor', 'middle')
          .text(line)
          .style('font-size', '12px')
          .style('font-weight', '700')
          .style('fill', '#1a1a1a');
      });
    });
  }, [data, selectedPerson, onPersonClick, dimensions, isReady]);

  // Print handler
  const handlePrint = () => {
    const svgElement = svgRef.current;
    if (!svgElement) return;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the family tree');
      return;
    }
    
    // Clone the SVG
    const svgClone = svgElement.cloneNode(true);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Mwendwa Kitavi Family Tree</title>
          <style>
            @page { 
              size: landscape; 
              margin: 0.5cm; 
            }
            body { 
              margin: 0; 
              padding: 20px;
              font-family: Arial, sans-serif;
            }
            .header {
              text-align: center;
              margin-bottom: 10px;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              color: #333;
            }
            .legend {
              display: flex;
              justify-content: center;
              gap: 10px;
              margin-bottom: 15px;
              flex-wrap: wrap;
            }
            .legend-item {
              display: flex;
              align-items: center;
              gap: 4px;
              font-size: 11px;
            }
            .legend-color {
              width: 16px;
              height: 16px;
              border-radius: 4px;
            }
            svg {
              width: 100%;
              height: auto;
              max-height: 90vh;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Mwendwa Kitavi Family Tree</h1>
          </div>
          <div class="legend">
            <div class="legend-item"><div class="legend-color" style="background:#A0A0A0"></div>Ancestors</div>
            <div class="legend-item"><div class="legend-color" style="background:#00FFFF"></div>Mwendwa Kitavi</div>
            <div class="legend-item"><div class="legend-color" style="background:#FFB6C1"></div>Spouse</div>
            <div class="legend-item"><div class="legend-color" style="background:#DDA0DD"></div>Kaweto</div>
            <div class="legend-item"><div class="legend-color" style="background:#98FB98"></div>Children</div>
            <div class="legend-item"><div class="legend-color" style="background:#FFFACD"></div>Grandchildren</div>
            <div class="legend-item"><div class="legend-color" style="background:#FFE4B5"></div>Great-Grand</div>
          </div>
          ${svgClone.outerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return (
    <Box position="relative" style={{ width: '100%', height: '80vh', backgroundColor: '#f8f9fa' }} className="flowchart-container">
      {/* Generation Legend */}
      <Box
        style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          zIndex: 2000,
          backgroundColor: 'rgba(255,255,255,0.95)',
          padding: '6px 10px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          flexWrap: 'wrap',
          maxWidth: '70%'
        }}
      >
        <Typography style={{ fontSize: '11px', fontWeight: 600, color: '#666', marginRight: '4px' }}>Legend:</Typography>
        <Chip label="Ancestors" size="small" style={{ backgroundColor: '#A0A0A0', color: '#fff', height: '20px', fontSize: '10px' }} />
        <Chip label="Mwendwa" size="small" style={{ backgroundColor: '#00FFFF', color: '#000', height: '20px', fontSize: '10px' }} />
        <Chip label="Spouse" size="small" style={{ backgroundColor: '#FFB6C1', color: '#000', height: '20px', fontSize: '10px' }} />
        <Chip label="Kaweto" size="small" style={{ backgroundColor: '#DDA0DD', color: '#000', height: '20px', fontSize: '10px' }} />
        <Chip label="Children" size="small" style={{ backgroundColor: '#98FB98', color: '#000', height: '20px', fontSize: '10px' }} />
        <Chip label="G.Children" size="small" style={{ backgroundColor: '#FFFACD', color: '#000', height: '20px', fontSize: '10px' }} />
      </Box>

      {/* Control Buttons */}
      <Box
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          display: 'flex',
          gap: '8px',
          zIndex: 2000,
        }}
      >
        <IconButton
          onClick={handlePrint}
          style={{
            backgroundColor: 'rgba(255,255,255,0.95)',
            color: '#333',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
          aria-label="Print Family Tree"
          title="Print Family Tree"
        >
          <PrintIcon />
        </IconButton>
        {selectedPerson && (
          <IconButton
            onClick={() => {
              onBack();
            }}
            style={{
              backgroundColor: 'rgba(255,255,255,0.95)',
              color: '#333',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            aria-label="Close Flowchart View"
            title="Close"
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%', overflow: 'hidden' }}
      >
        {!isReady ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <Typography>Loading flowchart...</Typography>
          </Box>
        ) : (
          <svg ref={svgRef}></svg>
        )}
      </div>
      
      {/* Instructions */}
      <Box
        style={{
          position: 'absolute',
          bottom: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(255,255,255,0.9)',
          padding: '4px 12px',
          borderRadius: '4px',
          zIndex: 2000,
        }}
      >
        <Typography style={{ fontSize: '11px', color: '#666' }}>
          Scroll to zoom • Drag to pan • Click node to drill down
        </Typography>
      </Box>
    </Box>
  );
};

export default FlowChartView2;